/**
 * Cloud Browser-Side WebRTC Stats Collector
 *
 * Collects RTCPeerConnection.getStats() for each camera track.
 * Adapted from local-node's webrtcBrowserMonitor.js for the cloud frontend.
 *
 * - Debug mode toggled via a button, persisted in localStorage
 * - Polls getStats() every 2s when debug is active
 * - Maps inbound-rtp reports to cameras via track/stream ID naming convention
 */

// ─── Debug Mode ──────────────────────────────────────────────────
const DEBUG_MODE_KEY = 'cloud_trinetra_debug_mode';

let _debugMode = (() => {
  try { return localStorage.getItem(DEBUG_MODE_KEY) === 'true'; } catch { return false; }
})();

const _debugListeners = new Set<(enabled: boolean) => void>();

function _notifyDebug() {
  _debugListeners.forEach(fn => { try { fn(_debugMode); } catch { /* */ } });
}

export function isDebugMode(): boolean { return _debugMode; }

export function setDebugMode(enabled: boolean): void {
  _debugMode = !!enabled;
  try { localStorage.setItem(DEBUG_MODE_KEY, _debugMode ? 'true' : 'false'); } catch { /* */ }
  _notifyDebug();
  _notifyStats();
}

export function subscribeDebugMode(listener: (enabled: boolean) => void): () => void {
  _debugListeners.add(listener);
  return () => { _debugListeners.delete(listener); };
}

// ─── Per-Camera Stats Store ──────────────────────────────────────
export interface CameraStatsSnapshot {
  timestamp: number;
  framesPerSecond: number;
  bitrateKbps: number;
  rtt: number;
  jitter: number;
  packetLossRate: number;
  frameWidth: number;
  frameHeight: number;
  totalPacketsReceived: number;
  totalPacketsLost: number;
  totalFramesDecoded: number;
  totalFramesDropped: number;
  totalBytesReceived: number;
  nackCount: number;
  pliCount: number;
  freezeCount: number;
}

interface CameraEntry {
  latest: CameraStatsSnapshot | null;
  prevRaw: RawInboundStats | null;
}

interface RawInboundStats {
  timestamp: number;
  packetsReceived: number;
  packetsLost: number;
  bytesReceived: number;
  framesDecoded: number;
  framesDropped: number;
  framesPerSecond: number;
  jitter: number;
  jitterBufferDelay: number;
  jitterBufferEmittedCount: number;
  nackCount: number;
  pliCount: number;
  firCount: number;
  freezeCount: number;
  frameWidth: number;
  frameHeight: number;
  rtt: number;
}

const _cameraStats = new Map<string, CameraEntry>();
const _statsListeners = new Set<() => void>();

function _notifyStats() {
  _statsListeners.forEach(fn => { try { fn(); } catch { /* */ } });
}

export function subscribeStats(listener: () => void): () => void {
  _statsListeners.add(listener);
  return () => { _statsListeners.delete(listener); };
}

export function getCameraStats(cameraId: string): CameraStatsSnapshot | null {
  return _cameraStats.get(cameraId)?.latest || null;
}

export function getAllCameraStats(): Record<string, CameraStatsSnapshot> {
  const result: Record<string, CameraStatsSnapshot> = {};
  _cameraStats.forEach((val, key) => { if (val.latest) result[key] = val.latest; });
  return result;
}

// ─── Stats Collector ─────────────────────────────────────────────
// Collectors are keyed because a page can watch more than one peer connection at a
// time — the Multi-Site View grid holds cameras from several nodes, each with its own
// PC, and stats for a node are only in that node's PC. The unkeyed
// attachCloudMonitor/stopCloudMonitor pair is the single-PC case (one node in
// context) and is just the DEFAULT_KEY entry.
const _collectors = new Map<string, CloudStatsCollector>();
const DEFAULT_KEY = '__default__';

export function attachCloudMonitorFor(key: string, pc: RTCPeerConnection): CloudStatsCollector {
  const existing = _collectors.get(key);
  // Re-attaching the same PC is a no-op: callers reconcile on a timer, and tearing the
  // collector down each pass would throw away the previous sample every poll — every
  // rate (bitrate, loss) is a delta against it, so they'd all read 0.
  if (existing) {
    // A closed PC also falls through to a rebuild: collect() stops itself on close, so
    // keeping that entry would leave the node silently un-monitored after a reconnect.
    if (existing.peerConnection === pc && pc.connectionState !== 'closed') return existing;
    existing.stop();
  }
  const collector = new CloudStatsCollector(pc);
  _collectors.set(key, collector);
  collector.start();
  return collector;
}

export function stopCloudMonitorFor(key: string): void {
  const collector = _collectors.get(key);
  if (collector) { collector.stop(); _collectors.delete(key); }
}

/** Keys of the currently attached collectors — lets callers reconcile against live nodes. */
export function attachedMonitorKeys(): string[] {
  return [..._collectors.keys()];
}

export function attachCloudMonitor(pc: RTCPeerConnection): CloudStatsCollector {
  return attachCloudMonitorFor(DEFAULT_KEY, pc);
}

export function stopCloudMonitor(): void {
  stopCloudMonitorFor(DEFAULT_KEY);
}

class CloudStatsCollector {
  private pc: RTCPeerConnection;
  private timer: ReturnType<typeof setInterval> | null = null;
  private interval = 2000;
  private debugUnsub: (() => void) | null = null;

  /** The PC this collector is bound to, so a re-attach can skip an identical one. */
  get peerConnection(): RTCPeerConnection { return this.pc; }

  constructor(pc: RTCPeerConnection) {
    this.pc = pc;
    this.debugUnsub = subscribeDebugMode((enabled) => {
      if (enabled) this.startCollection();
      else this.stopCollection();
    });
  }

  start(): void {
    if (isDebugMode()) this.startCollection();
  }

  private startCollection(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.collect(), this.interval);
    this.collect();
  }

  private stopCollection(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  stop(): void {
    this.stopCollection();
    if (this.debugUnsub) { this.debugUnsub(); this.debugUnsub = null; }
  }

  private async collect(): Promise<void> {
    if (!this.pc || this.pc.connectionState === 'closed') { this.stop(); return; }
    if (!isDebugMode()) return;

    try {
      const rawStats = await this.pc.getStats();
      const parsed = this.parseStatsByCamera(rawStats);

      for (const [cameraId, raw] of Object.entries(parsed)) {
        let entry = _cameraStats.get(cameraId);
        if (!entry) { entry = { latest: null, prevRaw: null }; _cameraStats.set(cameraId, entry); }
        entry.latest = this.computeDeltas(raw, entry.prevRaw);
        entry.prevRaw = raw;
      }
      _notifyStats();
    } catch { /* PC might be closing */ }
  }

  private parseStatsByCamera(rawStats: RTCStatsReport): Record<string, RawInboundStats> {
    const byCamera: Record<string, RawInboundStats> = {};

    // Find best candidate pair for RTT
    let bestPair: any = null;
    rawStats.forEach((report: any) => {
      if (report.type === 'candidate-pair' && (report.state === 'succeeded' || report.nominated)) {
        if (!bestPair || (report.currentRoundTripTime || 0) < (bestPair.currentRoundTripTime || Infinity)) {
          bestPair = report;
        }
      }
    });

    rawStats.forEach((report: any) => {
      if (report.type === 'inbound-rtp' && (report.kind === 'video' || report.mediaType === 'video')) {
        let cameraId: string | null = null;

        // Track ID naming: "video-{cameraId}"
        if (report.trackIdentifier?.startsWith('video-')) {
          cameraId = report.trackIdentifier.substring(6);
        }

        // Fallback: match via transceiver mid
        if (!cameraId && report.mid != null && this.pc.getTransceivers) {
          try {
            const t = this.pc.getTransceivers().find((tr: RTCRtpTransceiver) => tr.mid === String(report.mid));
            if (t?.receiver?.track?.id?.startsWith('video-')) {
              cameraId = t.receiver.track.id.substring(6);
            }
          } catch { /* */ }
        }

        if (!cameraId) return;

        byCamera[cameraId] = {
          timestamp: Date.now(),
          packetsReceived: report.packetsReceived || 0,
          packetsLost: report.packetsLost || 0,
          bytesReceived: report.bytesReceived || 0,
          framesDecoded: report.framesDecoded || 0,
          framesDropped: report.framesDropped || 0,
          framesPerSecond: report.framesPerSecond || 0,
          jitter: report.jitter || 0,
          jitterBufferDelay: report.jitterBufferDelay || 0,
          jitterBufferEmittedCount: report.jitterBufferEmittedCount || 0,
          nackCount: report.nackCount || 0,
          pliCount: report.pliCount || 0,
          firCount: report.firCount || 0,
          freezeCount: report.freezeCount || 0,
          frameWidth: report.frameWidth || 0,
          frameHeight: report.frameHeight || 0,
          rtt: bestPair?.currentRoundTripTime || 0,
        };
      }
    });

    return byCamera;
  }

  private computeDeltas(current: RawInboundStats, prev: RawInboundStats | null): CameraStatsSnapshot {
    const dt = prev ? (current.timestamp - prev.timestamp) / 1000 : this.interval / 1000;
    const packetsReceivedDelta = current.packetsReceived - (prev?.packetsReceived || 0);
    const packetsLostDelta = Math.max(0, current.packetsLost - (prev?.packetsLost || 0));
    const bytesReceivedDelta = current.bytesReceived - (prev?.bytesReceived || 0);
    const totalPktDelta = packetsReceivedDelta + packetsLostDelta;

    return {
      timestamp: current.timestamp,
      framesPerSecond: current.framesPerSecond || 0,
      bitrateKbps: dt > 0 ? Math.round((bytesReceivedDelta * 8) / (dt * 1000)) : 0,
      rtt: Math.round((current.rtt || 0) * 1000),
      jitter: Math.round((current.jitter || 0) * 1000),
      packetLossRate: totalPktDelta > 0 ? packetsLostDelta / totalPktDelta : 0,
      frameWidth: current.frameWidth || 0,
      frameHeight: current.frameHeight || 0,
      totalPacketsReceived: current.packetsReceived,
      totalPacketsLost: current.packetsLost,
      totalFramesDecoded: current.framesDecoded,
      totalFramesDropped: current.framesDropped,
      totalBytesReceived: current.bytesReceived,
      nackCount: current.nackCount,
      pliCount: current.pliCount,
      freezeCount: current.freezeCount,
    };
  }
}

// ─── One-shot capture for PacketMonitor ──────────────────────────
export interface CapturedStats {
  packetsReceived: number;
  bytesReceived: number;
  packetsLost: number;
  framesDecoded: number;
  framesDropped: number;
  jitter: number;
  jitterBufferDelay: number;
  jitterBufferEmittedCount: number;
}

export async function captureStatsForCamera(pc: RTCPeerConnection, cameraId: string): Promise<CapturedStats | null> {
  if (!pc || pc.connectionState === 'closed') return null;

  try {
    const rawStats = await pc.getStats();
    let result: CapturedStats | null = null;

    rawStats.forEach((report: any) => {
      if (report.type !== 'inbound-rtp') return;
      if (report.kind !== 'video' && report.mediaType !== 'video') return;

      let matchId: string | null = null;
      if (report.trackIdentifier?.startsWith('video-')) matchId = report.trackIdentifier.substring(6);

      if (matchId === cameraId) {
        result = {
          packetsReceived: report.packetsReceived || 0,
          bytesReceived: report.bytesReceived || 0,
          packetsLost: report.packetsLost || 0,
          framesDecoded: report.framesDecoded || 0,
          framesDropped: report.framesDropped || 0,
          jitter: report.jitter || 0,
          jitterBufferDelay: report.jitterBufferDelay || 0,
          jitterBufferEmittedCount: report.jitterBufferEmittedCount || 0,
        };
      }
    });

    return result;
  } catch { return null; }
}
